import { useMemo, useState, useCallback } from "react";
import { api } from "../../../core/api/queries";
import { useReportPage } from "../../../shared/hooks";
import { ReportPageLayout } from "../../../shared/components/layouts";
import { EditionSelector } from "../../../shared/components/tables";
import { useUsersMutations } from "../hooks/use-users-mutations";
import { columnBuilder } from "../../../shared/utils";
import { NotesDialog } from "../components/NotesDialog";
import { UserDataPreviewDialog } from "../components/UserDataPreviewDialog";
import type { UserReportDTO } from "../api/users.api";
import { usersApi } from "../api/users.api";
import type { UserProfileDto } from "../../../api/generated/types.gen";
import { useAuthProviderV2, useRoutesProvider } from "../../../providers/provider-hooks";

/**
 * Users Report Page Props
 */
interface UsersReportPageProps {
    /** Initial filter to apply (e.g., { role: 'ELEV' } for student-only view) */
    initialFilter?: Partial<UserReportDTO>;
    /** Show simplified view with limited columns (for professor viewing students) */
    simplifiedView?: boolean;
}

/**
 * Users Report Page
 *
 * Displays all registered users with filtering, sorting, and admin-only actions.
 * Role-based columns: ADMIN users see password column and notes action button.
 *
 * Features:
 * - 15+ columns (13 common + 2 admin-only) OR 5 columns (simplified view)
 * - Phone call notes dialog (admin only, hidden in simplified view)
 * - Server-side filtering
 * - Excel export
 *
 * Props-based configuration:
 * - No props = Full admin view (all columns, all users)
 * - initialFilter={{ role: 'ELEV' }} = Pre-filtered to students
 * - simplifiedView={true} = Limited columns (isEnabled, timestamp, lastLogin, nume, email)
 *
 * Architecture (Refactored 2025-11-01):
 * - Uses useReportPage hook (consolidates useState, useQuery, useSpringReport, useReportExport)
 * - Uses ReportPageLayout component (consolidates JSX structure)
 * - Uses columnBuilder for column definitions
 * - Page-specific: NotesDialog state management, mutations, role-based columns
 * - Reduced from 234 lines to ~165 lines (29% reduction)
 */
export function UsersReportPage({ initialFilter, simplifiedView = false }: UsersReportPageProps = {}) {
    // ==========================================
    // 1. CUSTOM DATE FORMATTER
    // ==========================================

    /**
     * Formats Date object, ISO 8601 string, or Unix timestamp to custom date format: dd:MM:yy HH:MM
     * @example new Date('2025-02-01T14:30:00') -> "01:02:25 14:30"
     * @example "2025-02-01T14:30:00" -> "01:02:25 14:30" (ISO 8601 string)
     * @example 1738331400 -> "01:02:25 14:30" (Unix timestamp in seconds)
     */
    const formatCustomDate = useCallback((input: Date | number | string | null | undefined): string => {
        // console.log('[formatCustomDate] Input:', input, 'Type:', typeof input, 'instanceof Date:', input instanceof Date);

        if (!input) {
            // console.log('[formatCustomDate] Input is falsy, returning "-"');
            return "-";
        }

        // Handle Date objects, ISO 8601 strings, and Unix timestamps
        const date =
            input instanceof Date
                ? input
                : typeof input === "string"
                  ? new Date(input) // Parse ISO 8601 string directly
                  : new Date(input * 1000); // Unix timestamp (seconds → milliseconds)
        // console.log('[formatCustomDate] Date object:', date, 'getTime():', date.getTime(), 'Valid:', !isNaN(date.getTime()));

        // Validation check for invalid dates
        if (isNaN(date.getTime())) {
            // console.log('[formatCustomDate] Invalid date, returning "-"');
            return "-";
        }

        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
        const year = String(date.getFullYear()).slice(-2); // Last 2 digits
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");

        const result = `${day}-${month}-${year} ${hours}:${minutes}`;
        // console.log('[formatCustomDate] Result:', result);
        return result;
    }, []);

    // ==========================================
    // 2. DATA FETCHING & REPORT INTERFACE
    // ==========================================

    const {
        report,
        adapters,
        handleExport,
        setFilter,
        setPageable,
        editions,
        currentEdition,
        changeEdition,
        isAllEditions,
    } = useReportPage({
        queryHook: api.users.report,
        // Adminul urmareste inscrierile: cele mai noi conturi sus (cerinta ECOTIC, 2026-10-02).
        // Lista de elevi a profesorului ramane pe ultima logare.
        initialSort: [simplifiedView ? "lastLogin,desc" : "timestamp,desc"],
        initialFilter: initialFilter || {},
        exportEndpoint: "/api/users/report",
        exportFilename: simplifiedView ? "raport_elevi" : "raport_utilizatori",
        withEditions: true, // Enable edition filtering for ADMIN users
        allowAllEditions: true, // ADMIN poate cere "Toate edițiile" — vezi UserActivationReportHandler
    });

    // ==========================================
    // 3. PAGE-SPECIFIC STATE (NOTES DIALOG)
    // ==========================================

    const [isNoteDialogOpened, setIsNoteDialogOpened] = useState(false);
    const [noteInDialog, setNoteInDialog] = useState<string | null>(null);
    const [note, setNote] = useState("");

    // ==========================================
    // 4. PREVIEW DIALOG STATE
    // ==========================================

    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [loadingPreviewData, setLoadingPreviewData] = useState(false);
    const [previewData, setPreviewData] = useState<UserProfileDto | null>(null);

    // ==========================================
    // 5. PAGE-SPECIFIC MUTATIONS
    // ==========================================

    const mutations = useUsersMutations();

    // ==========================================
    // 6. DIALOG HANDLERS
    // ==========================================

    const openNotesDialog = useCallback((id: string, noteText?: string) => {
        setIsNoteDialogOpened(true);
        setNoteInDialog(id);
        setNote(noteText || "");
    }, []);

    const closeNotesDialog = useCallback(() => {
        setIsNoteDialogOpened(false);
        setNoteInDialog(null);
        setNote("");
    }, []);

    const saveNote = useCallback(async () => {
        if (!noteInDialog) return;

        await mutations.updatePhoneNote.mutateAsync({
            userId: noteInDialog,
            phoneCallNote: note,
        });

        // Close dialog on success (mutations hook handles toasts and cache invalidation)
        closeNotesDialog();
    }, [noteInDialog, note, mutations, closeNotesDialog]);

    // ==========================================
    // 7. PREVIEW DIALOG HANDLERS
    // ==========================================

    /**
     * Opens preview dialog with user profile data (includes nested school, parental approval, and school request)
     * Uses single unified /api/users/profile endpoint with userId and editionId parameters
     *
     * Refactored 2025-11-04: Simplified from 80 lines to 25 lines (70% reduction)
     * - Removed parallel fetching of multiple endpoints
     * - Removed complex fallback logic for protocol PDFs
     * - Single API call now returns all nested data (school, parentalApproval, schoolRequest)
     */
    const openPreviewDialog = useCallback(
        async (userId: string) => {
            const editionId = currentEdition?.id;

            if (!editionId) {
                alert("Nu există o ediție selectată");
                return;
            }

            setLoadingPreviewData(true);
            try {
                const profile = await usersApi.getProfile({ userId, editionId });
                setPreviewData(profile);
                setPreviewDialogOpen(true);
            } catch (error: unknown) {
                console.error("Error loading profile data:", error);
                alert("Eroare la încărcarea datelor. Vă rugăm încercați din nou.");
            } finally {
                setLoadingPreviewData(false);
            }
        },
        [currentEdition],
    );

    const closePreviewDialog = useCallback(() => {
        setPreviewDialogOpen(false);
        setPreviewData(null);
    }, []);

    // Override resetFilters to respect initialFilter; revine si la editia curenta (nu ramane
    // blocat pe "toate edițiile" dupa un reset)
    const resetFilters = useCallback(() => {
        setFilter(initialFilter || {});
        setPageable((p) => ({ ...p, page: 0 }));
        if (currentEdition?.id && changeEdition) {
            changeEdition(currentEdition.id);
        }
    }, [initialFilter, setFilter, setPageable, currentEdition, changeEdition]);

    // ==========================================
    // 8. ROLE-BASED LOGIC
    // ==========================================

    const { userInfo } = useAuthProviderV2();
    const { ROLES } = useRoutesProvider();
    const isAdmin = userInfo?.role === ROLES.ADMIN;
    // „Ultim cod” (2026-09-26): backendul trimite codul doar catre ADMIN/DBEST; la ceilalti campul vine gol.
    const canSeeOtp = userInfo?.role === ROLES.ADMIN || userInfo?.role === ROLES.DBEST;
    const canViewPreviews = userInfo?.role && [ROLES.ADMIN, ROLES.DBEST, ROLES.PROFESOR].includes(userInfo.role);

    // ==========================================
    // 9. COLUMN DEFINITIONS
    // ==========================================

    const columns = useMemo(() => {
        // Simplified view: Show only 4 essential columns (for professors viewing students)
        if (simplifiedView) {
            return columnBuilder<UserReportDTO>()
                .custom({
                    key: "timestamp",
                    label: "Data înscriere",
                    sortable: true,
                    filterable: false,
                    render: (_, row) => {
                        // console.log('[timestamp render] row.timestamp:', row.timestamp, 'row.id:', row.id, 'row.nume:', row.nume);
                        const result = formatCustomDate(row.timestamp);
                        // console.log('[timestamp render] Formatted result:', result);
                        return result;
                    },
                })
                .custom({
                    key: "lastLogin",
                    label: "Data ultima logare",
                    sortable: true,
                    filterable: false,
                    render: (_, row) => {
                        console.log(
                            "[lastLogin render] row.lastLogin:",
                            row.lastLogin,
                            "row.id:",
                            row.id,
                            "row.nume:",
                            row.nume,
                        );
                        const result = formatCustomDate(row.lastLogin);
                        console.log("[lastLogin render] Formatted result:", result);
                        return result;
                    },
                })
                .text("nume", "Nume", "Nume...", { cardRole: "title" })
                .text("email", "Email", "Email...")
                .build();
        }

        // Full view: All columns with admin-only features
        return (
            columnBuilder<UserReportDTO>()
                // ADMIN/DBEST/PROFESOR ONLY: Preview column (MOVED TO FIRST POSITION)
                .when(!!canViewPreviews, (b) =>
                    b.action("preview", "Previzualizare", (_, row) => {
                        const hasData = row.hasParentalApproval || row.hasSchoolRegistrationFormCompleted;

                        if (!hasData) {
                            return <span className="text-muted-foreground text-xs">-</span>;
                        }

                        return (
                            <button
                                disabled={report.loading || loadingPreviewData || !row.id}
                                onClick={() => row.id && openPreviewDialog(row.id)}
                                // Zona de click urcata de la 17,1 px la 25,1 px prin `::after`, fara
                                // sa creasca randul: cei 4 px de sus si de jos stau in padding-ul
                                // celulei (8 px), deci layout-ul nu se misca. Vezi nota de la
                                // butonul de notite pentru de ce nu 44.
                                className="relative after:absolute after:inset-x-0 after:-inset-y-1 font-bold text-nowrap hover:underline focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:no-underline">
                                Afișează
                            </button>
                        );
                    }),
                )
                .custom({
                    key: "timestamp",
                    label: "Data înscriere",
                    sortable: true,
                    filterable: false,
                    render: (_, row) => {
                        console.log(
                            "[timestamp render FULL] row.timestamp:",
                            row.timestamp,
                            "row.id:",
                            row.id,
                            "row.nume:",
                            row.nume,
                        );
                        const result = formatCustomDate(row.timestamp);
                        console.log("[timestamp render FULL] Formatted result:", result);
                        return result;
                    },
                })
                .custom({
                    key: "lastLogin",
                    label: "Data ultima logare",
                    sortable: true,
                    filterable: false,
                    render: (_, row) => {
                        console.log(
                            "[lastLogin render FULL] row.lastLogin:",
                            row.lastLogin,
                            "row.id:",
                            row.id,
                            "row.nume:",
                            row.nume,
                        );
                        const result = formatCustomDate(row.lastLogin);
                        console.log("[lastLogin render FULL] Formatted result:", result);
                        return result;
                    },
                })
                .when(isAdmin && isAllEditions, (b) =>
                    b.text("editionNume", "Ediție", "Ediție..."),
                )
                .when(canSeeOtp, (b) =>
                    b.custom({
                        key: "ultimCodOtp",
                        label: "Ultim cod",
                        // Backendul sorteaza dupa DATA ultimului cod, nu dupa cod; fara cod = la coada.
                        sortable: true,
                        filterable: false,
                        render: (_, row) =>
                            row.ultimCodOtp || row.ultimCodOtpDt ? (
                                <div className="flex flex-col">
                                    <span className="font-mono tabular-nums">{row.ultimCodOtp || "-"}</span>
                                    <span className="text-xs text-nowrap">{formatCustomDate(row.ultimCodOtpDt)}</span>
                                </div>
                            ) : (
                                "-"
                            ),
                    }),
                )
                .text("nume", "Nume", "Nume...", { cardRole: "title" })
                .text("email", "Email", "Email...")
                .text("role", "Rol", "Rol...")
                .text("telefon", "Telefon", "Telefon...", {
                    // 2026-09-28: telefonul se scrie pe cont la inscriere, inainte de verificarea
                    // emailului (owner-ul il vrea vizibil, nu ascuns pana la verificare) -- dar
                    // ramane inert ca identificator de login pana atunci (findByTelefonForOtp cere
                    // emailVerifiedDt). Marcajul de aici e doar vizual, ca sa nu se confunde cu un
                    // numar confirmat.
                    render: (_, row) =>
                        row.telefon ? (
                            row.emailVerifiedDt ? (
                                row.telefon
                            ) : (
                                <span>
                                    {row.telefon}{" "}
                                    <span className="text-xs text-muted-foreground">(neconfirmat)</span>
                                </span>
                            )
                        ) : (
                            "-"
                        ),
                })
                .text("clasa", "Clasa", "Clasa...")
                .boolean("hasSchoolRegistrationFormCompleted", "Formular Complet")
                .text("rejectReason", "Motiv Respingere", "Motiv...")
                .text("phoneCallNote", "Nota Telefonica", "Nota...")
                .nested(
                    "school.numeInstitutieInvatamant",
                    "Nume Instituție",
                    (row) => row.school?.numeInstitutieInvatamant,
                    { filterPlaceholder: "Nume instituție..." },
                )
                .nested("school.adresa.judet", "Județ", (row) => row.school?.adresa?.judet, {
                    filterPlaceholder: "Județ...",
                })
                .nested("school.adresa.oras", "Oraș", (row) => row.school?.adresa?.oras, {
                    filterPlaceholder: "Oraș...",
                })
                // ADMIN ONLY: Notes action column
                .when(isAdmin, (b) =>
                    b.action("actions_notes", "Acțiune Notițe", (_, row) => (
                        <button
                            disabled={report.loading || !row.id}
                            onClick={() => row.id && openNotesDialog(row.id, row.phoneCallNote)}
                            // Masurat la 1280 px: butonul era 86,5 x 17,1 px, cu zona de click tot
                            // 17,1. `::after` o duce la 25,1 — peste pragul WCAG 2.2 AA de 24 px —
                            // ramanand in padding-ul de 8 px al celulei, deci randul ramane 33 px.
                            // 44 px NU se poate: ar cere 13,5 px sus si jos, iar intre doua randuri
                            // sunt doar 16 px de padding, deci zonele a doua butoane vecine s-ar
                            // suprapune si si-ar fura click-urile.
                            className="relative after:absolute after:inset-x-0 after:-inset-y-1 font-bold text-nowrap hover:underline focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:no-underline">
                            Adaugă Notițe
                        </button>
                    )),
                )
                .build()
        );
    }, [
        simplifiedView,
        isAdmin,
        canSeeOtp,
        isAllEditions,
        canViewPreviews,
        report.loading,
        loadingPreviewData,
        openNotesDialog,
        openPreviewDialog,
        formatCustomDate,
    ]);

    // ==========================================
    // 10. RENDER
    // ==========================================

    // Create report override with custom resetFilters
    const reportWithOverrides = useMemo(
        () => ({
            ...report,
            resetFilters,
        }),
        [report, resetFilters],
    );

    return (
        <>
            <ReportPageLayout
                title={simplifiedView ? "Raport Conturi Elevi" : "Raport Utilizatori"}
                report={reportWithOverrides}
                adapters={adapters}
                handleExport={handleExport}
                columns={columns}
                entityLabel={simplifiedView ? "elevi" : "utilizatori"}
                extraControls={
                    isAdmin ? (
                        <EditionSelector
                            editions={editions || []}
                            currentEditionId={currentEdition?.id}
                            onChange={changeEdition || (() => {})}
                            disabled={report.loading}
                            allowAllEditions
                            isAllEditionsSelected={isAllEditions}
                        />
                    ) : undefined
                }
            />

            {/* Notes Dialog (Admin only, hidden in simplified view) */}
            {!simplifiedView && isAdmin && (
                <NotesDialog
                    isOpen={isNoteDialogOpened}
                    onClose={closeNotesDialog}
                    onSave={saveNote}
                    note={note}
                    onChange={setNote}
                    errors={{}}
                    disabled={mutations.isUpdatingPhoneNote}
                />
            )}

            {/* Preview Dialog (Admin/DBEST/Profesor only) */}
            <UserDataPreviewDialog
                isOpen={previewDialogOpen}
                onClose={closePreviewDialog}
                data={previewData}
                isLoading={loadingPreviewData}
            />
        </>
    );
}
